import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import styles from "./RemindersDrawer.module.scss";
import { Avatar } from "@fluentui/react-components";
import { EyeRegular, EyeOffRegular, AlertSnooze16Regular, AlertOff16Regular } from "@fluentui/react-icons";
import { Ellipsis, Trash2, Clock, Hourglass, AlarmClock, AlarmClockPlus, Dot } from 'lucide-react';
import service from "../../store/services/general.service";
import { generalActions } from "../../store/actions";
import { utcToLocalDateTimeString } from "../../utils/systemSettings";
import { DailySummary, DeadlineTask, UnassignedTask, CalendarIcon, MeetingsClockIcon } from "../../modules/default-svg";
import moment from "moment";
import Button from "../controls/button/Button";
import HoverCard from "../hoverCard/HoverCard";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getReminders } from "../../store/actions/general.actions";
import { api as generalService } from '../../store/services/general.service';
import { REDIRECTION_TYPES } from '../../helpers/utils';
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const RemindersCard = ({ item, setIsModalOpen, setReminder, modalType }) => {
  const dispatch = useDispatch();
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  let dateFormat = settings?.regionalSettings?.dateFormat;
  const timeFormat = "HH:mm";
  const [displayTitle, setDisplayTitle] = useState("");
  const [displayNotes, setDisplayNotes] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [hasLongContent, setHasLongContent] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSnoozePanel, setShowSnoozePanel] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [isSnoozeLoading, setIsSnoozeLoading] = useState(false);
  const [isUnSnoozeLoading, setIsUnSnoozeLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({ snoozeDate: null });
  const notesRef = useRef(null);
  const menuRef = useRef(null);
  const snoozeRef = useRef(null);
  const cardRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (snoozeRef.current && !snoozeRef.current.contains(event.target)) {
        setShowSnoozePanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const deleteReminder = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    service.deleteReminderRequest(item.id).then(() => {
      dispatch(getReminders());
    }).catch((error) => {

    });
  };

  const toggleReminderReadStatus = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    service.markReminderReadRequest({ payload: { Ids: [item.id], IsRead: !item.isRead } }).then(() => {
      dispatch(getReminders());
    }).catch((error) => {

    });
  };

  const handleSnooze = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowSnoozePanel(!showSnoozePanel);

    if (!showSnoozePanel) {
      if (item?.snoozedUntil) {
        // Convert UTC to local time for display
        const localMoment = moment.utc(item.snoozedUntil).local();
        setDate(localMoment.toDate());
        setTime(localMoment.toDate());
      } else {
        // Set default to 1 hour from now
        const oneHourFromNow = moment().add(1, 'hour').toDate();
        setDate(oneHourFromNow);
        setTime(oneHourFromNow);
      }
    }
  };

  const handleSaveSnooze = () => {
    if (!date) {
      setFormErrors({ snoozeDate: "Please select date!" });
      return;
    }

    // Combine date and time
    const combinedDateTime = moment(date)
      .hour(moment(time).hour())
      .minute(moment(time).minute());

    setIsSnoozeLoading(true);
    service.reminderSnoozeRequest({
      id: item.id,
      payload: { SnoozedUntil: combinedDateTime.utc().format() }
    }).then(() => {
      setIsSnoozeLoading(false);
      setShowSnoozePanel(false);
      dispatch(getReminders());
    }).catch((error) => {
      setIsSnoozeLoading(false);
    });
  };

  const handleUnsnooze = () => {
    setIsUnSnoozeLoading(true);
    service.reminderSnoozeRequest({ id: item.id, payload: { SnoozedUntil: null } }).then(() => {
      setIsUnSnoozeLoading(false);
      setShowSnoozePanel(false);
      dispatch(getReminders());
    }).catch((error) => {
      setIsUnSnoozeLoading(false);
    });
  };

  const snoozeSuggestion = (type) => {
    setIsSnoozeLoading(true);
    const snoozeUntil = type === "tomorrow"
      ? moment().add(1, "day").startOf("day").hour(8).utc().toISOString()
      : moment().add(1, "hours").utc().toISOString();

    service.reminderSnoozeRequest({ id: item.id, payload: { SnoozedUntil: snoozeUntil } }).then(() => {
      setIsSnoozeLoading(false);
      setShowSnoozePanel(false);
      dispatch(getReminders());
    }).catch((error) => {
      setIsSnoozeLoading(false);
    });
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const getPriorityClass = () => {
    switch (item?.priority || 0) {
      case 2: return styles.priorityHigh;
      case 1: return styles.priorityModerate;
      default: return styles.priorityLow;
    }
  };

  const getNotificationIcon = () => {
    switch (item.notificationType) {
      case "unassignedTask": return <UnassignedTask />;
      case "taskDeadline": return <DeadlineTask />;
      case "dailySummary": return <DailySummary />;
      default: return <UnassignedTask />;
    }
  };

  const getIconColor = () => {
    switch (item.notificationType) {
      case "unassignedTask": return "blue";
      case "taskDeadline": return "red";
      case "dailySummary": return "beige";
      default: return "blue";
    }
  };

  const timeDifference = (date) => {
    if (!date) return '';
    const now = new Date();
    const givenDate = new Date(date);
    const seconds = Math.abs(Math.floor((now - givenDate) / 1000));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return `now`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const isFutureDate = (dateString) => {
    if (!dateString) return false;
    return moment.utc(dateString).isAfter(moment());
  };

  const timeDifferenceFromNow = (dateString) => {
    const now = moment();
    const duration = moment.duration(moment.utc(dateString).diff(now));
    const days = Math.floor(duration.asDays());
    const hours = Math.floor(duration.asHours() % 24);
    const minutes = Math.floor(duration.asMinutes() % 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return `in ${parts.join(' ') || ''}`;
  };

  const transformLinks = (text, links) => {
      if (!text || text.trim() === "" || !text.includes("<@link:"))
        return text;
  
      const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
      const transformedString = text.replace(regex, (match, type, id) => {
        let entityName;
        if (links && links?.length > 0) {
          entityName = links.filter((e) => e.linkId === id).map((e) => e.linkName);
        }
        let linkUrl = "";
        if (type === "Client" || type === "Project" || type === "Scope")
          linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}s/${id}/overview`;
        else if (type === "Portfolio" || type === "Program")
          linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}/${id}/overview`;
        else if (type === "ChangeRequest" || type === "CommunicationPlan" || type === "Budget" || type === "Risk" || type === "Task")
          return `<a target="_blank" class="notification-redirection-link cursor-pointer" data-type="${type.toLowerCase()}" data-id="${id}">${entityName}</a>`;
  
        if (linkUrl !== "")
          return `<a href=${linkUrl} target="_self" data-type="${type}" data-id="${id}">${entityName}</a>`;
        else
          return `<span class="text-primary">${entityName}</span>`;
      });
      return transformedString;
    }
  
    useEffect(() => {
      const handleLinkClick = (e) => {
        const target = e.target;
        // Check if the clicked element has the redirection class
        if (target.classList.contains('notification-redirection-link')) {
          e.preventDefault();
          e.stopPropagation();
  
          const type = target.getAttribute('data-type');
          const id = target.getAttribute('data-id');
  
          if (type && id) {
            generalService.getNotificationLinkRedirectionRequest({ type, id })
              .then((response) => {
                if (response) {
                  if (response.scopeId)
                    handleScopeRedirection(response.scopeId, id);
  
                  else if (response.projectId)
                    handleProjectRedirection(response.projectId, type, id);
  
                }
              }).catch((error) => {
              });
          }
        }
      };
  
      const cardElement = cardRef.current;
      if (cardElement) {
        cardElement.addEventListener('click', handleLinkClick);
      }
  
      return () => {
        if (cardElement) {
          cardElement.removeEventListener('click', handleLinkClick);
        }
      };
    }, []);
    const handleScopeRedirection = (scopeId, taskId) => {
      history.push(`/scopes/${scopeId}/overview?r_id=${taskId}`);
    };
  
    const handleProjectRedirection = (projectId, type, id) => {
      if (projectId && type && id) {
        let redirectionType;
        switch (type) {
          case 'changerequest':
            redirectionType = REDIRECTION_TYPES.CHANGE_REQUEST;
            break;
          case 'communicationplan':
            redirectionType = REDIRECTION_TYPES.COMMUNICATION;
            break;
          case 'budget':
            redirectionType = REDIRECTION_TYPES.BUDGET;
            break;
          case 'risk':
            redirectionType = REDIRECTION_TYPES.RISK;
            break;
          case 'task':
            redirectionType = REDIRECTION_TYPES.TASK;
            break;
          default:
            return;
        }
  
        history.push(`/projects/${projectId}/overview?r_t=${redirectionType}&r_id=${id}`);
      }
  
    };

  useEffect(() => {
    setDisplayTitle(transformLinks(item?.title, item?.links));
    setDisplayNotes(transformLinks(item?.notes, item?.links));
  }, [item?.title, item?.notes]);

  useEffect(() => {
    if (notesRef.current && displayNotes) {
      const lineHeight = parseInt(getComputedStyle(notesRef.current).lineHeight);
      setHasLongContent(notesRef.current.scrollHeight > lineHeight * 2);
    }
  }, [displayNotes]);

  return (
    <div 
      ref={cardRef} 
      className={`${styles.remindCardWrapper} ${getPriorityClass()} ${expanded ? styles.expanded : ''} ${item?.isRead ? styles.isRead : ''}`}>
      <div className={styles.actionMenu} ref={menuRef}>
        <button className={styles.menuTrigger} onClick={toggleMenu}>
          <Ellipsis size={16} />
        </button>
        {showMenu && (
          <div className={styles.menuDropdown}>
            {
              !item.snoozedUntil && (
                <button className={styles.menuItem} onClick={toggleReminderReadStatus}>
                  {!item?.isRead ? (
                    <>
                      <EyeRegular style={{ fontSize: '16px' }} />
                      <span>Mark as read</span>
                    </>
                  ) : (
                    <>
                      <EyeOffRegular style={{ fontSize: '16px' }} />
                      <span>Mark as unread</span>
                    </>
                  )}
                </button>
              )
            }

            <button className={styles.menuItem} onClick={handleSnooze}>
              <AlertSnooze16Regular style={{ fontSize: '16px' }} />
              <span>Snooze</span>
            </button>
            {
              item?.snoozedUntil && (
                <button className={styles.menuItem} onClick={handleUnsnooze}>
                  <AlertOff16Regular style={{ fontSize: '16px' }} />
                  <span>Unsnooze</span>
                </button>
              )
            }
            <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={deleteReminder}>
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>

      {showSnoozePanel && (
        <div className={styles.snoozePanel} ref={snoozeRef}>
          <div className={styles.snoozePanelHeader}>
            <h4>Snooze Reminder</h4>
          </div>
          <div className={styles.snoozePanelContent}>
            <div className={styles.snoozeInputs}>
              <div className={styles.snoozeInputGroup}>
                <label className={styles.snoozeLabel}>Date</label>
                <div className={styles.snoozeInputWrapper}>
                  <DatePicker
                    selected={date}
                    onChange={setDate}
                    dateFormat={dateFormat}
                    minDate={new Date()}
                    className={styles.snoozeDatePicker}
                    popperClassName="react-datepicker-popper"
                    popperPlacement="bottom-start"
                  />
                  <CalendarIcon className={styles.snoozeInputIcon} />
                </div>
                {formErrors.snoozeDate && (
                  <span className={styles.errorMessage}>{formErrors.snoozeDate}</span>
                )}
              </div>

              <div className={styles.snoozeInputGroup}>
                <label className={styles.snoozeLabel}>Time</label>
                <div className={styles.snoozeInputWrapper}>
                  <DatePicker
                    selected={time}
                    onChange={setTime}
                    showTimeSelect
                    showTimeSelectOnly
                    timeFormat={timeFormat}
                    timeIntervals={15}
                    dateFormat={timeFormat}
                    className={styles.snoozeTimePicker}
                    popperClassName="react-datepicker-popper"
                    popperPlacement="bottom-start"
                    timeCaption="Time"
                  />
                  <MeetingsClockIcon className={styles.snoozeInputIcon} />
                </div>
              </div>
            </div>

            <div className={styles.snoozeSuggestions}>
              <p className={styles.suggestionsTitle}>Suggestions</p>
              <div className={styles.suggestionsList}>
                <button onClick={() => snoozeSuggestion("")}>
                  <span>Later</span>
                  <span className={styles.suggestionTime}>in 1 hour</span>
                </button>
                <button onClick={() => snoozeSuggestion("tomorrow")}>
                  <span>Tomorrow</span>
                  <span className={styles.suggestionTime}>at 8:00 am</span>
                </button>
              </div>
            </div>
            <div className={styles.snoozeActions}>
              {item?.snoozedUntil && (
                <Button label="Unsnooze" icon={<AlertOff16Regular />} btnClass={styles.unsnoozeBtn} showLoading={isUnSnoozeLoading} clickHandler={handleUnsnooze} />
              )}
              <Button label="Snooze" icon={<AlertSnooze16Regular />} btnClass={styles.snoozeBtn} showLoading={isSnoozeLoading} clickHandler={handleSaveSnooze} />
            </div>
          </div>
        </div>
      )}

      <div className={styles.avatarWrapper}>
        {item?.creator ? (
          <HoverCard
            HoverCardTrigger={
              <div>
                <Avatar
                  size={28}
                  color="colorful"
                  name={item?.creator.name}
                  image={{ src: item?.creator.image }}
                />
              </div>
            }
            AvatarId={item?.creator?.id}
            AvatarName={item?.creator?.name}
            AvatarImage={item?.creator?.image}
            AvatarDesignation={item?.creator?.title}
            AvatarEmail={item?.creator?.email}
            AvatarPhone={item?.creator?.phoneNumber}
            AvatarIsExternal={item?.creator?.isExternal}
            CanSendMessage={true}
          />
        ) : (
          <Avatar size={28} color={getIconColor()} icon={getNotificationIcon()} />
        )}
      </div>

      <div className={styles.remindCardContent}>
        <div className={styles.titleWrapper}>
          {displayTitle && (
            <p className={styles.reminderTitle}>
              <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
            </p>
          )}
          {displayNotes && (
            <>
              <p ref={notesRef} className={styles.reminderNotes}>
                <span dangerouslySetInnerHTML={{ __html: displayNotes }} />
              </p>
              {hasLongContent && (
                <button className={styles.readMoreButton} onClick={handleExpand}>
                  {expanded ? (
                    <>
                      Show less
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </>
                  ) : (
                    <>
                      Read more
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        <div className={styles.reminderMeta}>

          {item?.dueDate && (
            <span className={styles.dueDate}>
              <AlarmClock size={14} />
              {utcToLocalDateTimeString(setting, item?.dueDate)}
              <Dot size={10} />
              <span>{timeDifference(item?.dueDate)}</span>
            </span>
          )}
          {isFutureDate(item?.snoozedUntil) && timeDifferenceFromNow(item?.snoozedUntil) && (
            <span className={styles.snoozedBadge}>
              <AlarmClockPlus size={14} />
              Snoozed
              <Dot size={10} />
              {utcToLocalDateTimeString(setting, item?.snoozedUntil)}
              <Dot size={10} />
              <span>{timeDifferenceFromNow(item?.snoozedUntil)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(RemindersCard);
